/* eslint-disable @typescript-eslint/no-explicit-any */
import { TParsedField, TSchema } from 'types/items';
import moment from 'moment-timezone';

// This method is used for any 'New' dialogs when they want to get a default
// new record in a format that is expected by all of the widgets used in base
// module.
export const getInitialRecord = (fields: TParsedFieldWithFieldName[]) =>
  fields.reduce((acc: { [key: string]: any }, field) => {
    if (field.fieldName && field.type === 'datetime') {
      acc[field.fieldName] = undefined;
    } else if (field.fieldName && field.type === 'bool') {
      acc[field.fieldName] = false;
    } else if (field.fieldName && field.type === 'enum') {
      acc[field.fieldName] = null;
    } else if (field.fieldName && field.uiType === 'autocomplete') {
      acc[field.fieldName] = null;
    } else if (field.fieldName) {
      acc[field.fieldName] = '';
    }

    return acc;
  }, {});

export type TParsedFieldWithFieldName = TParsedField & { fieldName: string };

// This method is used to prepare records before pushing it into the integtation API.
// It ensures that any enums are being sent with their values instead of valueLabels,
// booleans are true / false, and datetimes are in ISO format.
export const prepareRecordsForSave = (
  fields: TParsedFieldWithFieldName[],
  schema: TSchema,
  records: any[]
) =>
  records.map((record) =>
    fields.reduce((acc: { [key: string]: any }, field) => {
      // Detect if record[field.fieldName] is and object that contains an id property.
      // If so, use that id property as the value for the field.
      if (
        field.fieldName &&
        record[field.fieldName] &&
        record[field.fieldName].id
      ) {
        acc[field.fieldName] = record[field.fieldName].id;
      } else if (field.fieldName && field.type === 'enum') {
        // Locate the valueLabel and return the associated id.
        const index = schema.fields[field.fieldName]?.valueLabels?.indexOf(
          record[field.fieldName]
        );
        if (index === undefined) {
          acc[field.fieldName] = record[field.fieldName];
        } else {
          acc[field.fieldName] =
            schema.fields[field.fieldName]?.values?.[index] ||
            record[field.fieldName];
        }
      } else if (field.fieldName && field.type === 'bool') {
        acc[field.fieldName] = record[field.fieldName] === true;
      } else if (field.fieldName && field.type === 'datetime') {
        if (!record[field.fieldName]) {
          acc[field.fieldName] = undefined;
        } else {
          acc[field.fieldName] = record[field.fieldName].toISOString();
        }
      } else if (field.fieldName) {
        acc[field.fieldName] = record[field.fieldName];
      }
      return acc;
    }, {})
  );

// This next function is used to take records loaded from API and prepare them
// for display in the UX.
export const prepareRecordsForDisplay = (
  fields: TParsedFieldWithFieldName[],
  schema: TSchema<any>,
  rows: any[]
) => {
  const updatedRows: any[] = [];
  rows.forEach((row) => {
    const updatedRow = { ...row };
    fields.forEach((field) => {
      if (field.fieldName && field.type === 'enum') {
        let index: number | undefined = schema.fields[
          field.fieldName
        ]?.values?.indexOf(updatedRow[field.fieldName]);
        if (index === undefined) {
          index = -1;
        }
        updatedRow[field.fieldName] =
          schema.fields[field.fieldName]?.valueLabels?.[index] ||
          updatedRow[field.fieldName];
      } else if (field.fieldName && field.uiType === 'autocomplete') {
        updatedRow[field.fieldName] = { id: row[field.fieldName] };
      } else if (field.fieldName && field.type === 'datetime') {
        if (!row[field.fieldName]) {
          updatedRow[field.fieldName] = undefined;
        } else {
          updatedRow[field.fieldName] = moment(row[field.fieldName]);
        }
      } else if (field.fieldName && field.type === 'string') {
        updatedRow[field.fieldName] = row[field.fieldName] || '';
      }
    });
    updatedRows.push(updatedRow);
  });
  return updatedRows;
};

// Validates text data according to schema
export const textFieldDataValid = (value: string, field: TParsedField) => {
  // Validate the new value doesn't exceed the max length as defined by
  // the fields size property for the field
  if (field.size && value.length > field.size) {
    return false;
  }

  // If the field type is 'int', validate that the value is either empty
  // or composed only of digits 0-9
  if (field.type === 'int' && value && !/^\d+$/.test(value)) {
    return false;
  }

  // If the field type is 'float', validate that the value is either empty
  // or composed only of digits 0-9 and a single decimal point
  if (field.type === 'float' && value && !/^[0-9]+(\.[0-9]*)?$/.test(value)) {
    return false;
  }

  return true;
};

export const isValidIPv4 = (ip: string): boolean => {
  if (!ip) return false;
  const ipv4Regex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipv4Regex.test(ip);
};

export const isValidPort = (port: string | number): boolean => {
  const portNum = typeof port === 'string' ? parseInt(port, 10) : port;
  return !Number.isNaN(portNum) && portNum >= 1 && portNum <= 65535;
};

export const isValidURL = (url: string): boolean => {
  if (!url) return true; // Empty is valid (optional field)
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

export const areEthernetFieldsRequired = (
  machineType: string | null,
  machineConnectionType?: string | null
): boolean => {
  if (
    machineType === 'CITIZEN' ||
    machineType === 'MITSUBISHI' ||
    machineType === 'FANUC' ||
    machineType === 'HEIDENHAIN' ||
    machineType === 'Hurco' ||
    machineType === 'MTCONNECT' ||
    machineType === 'OPCUA' ||
    machineType === 'BROTHER'
  ) {
    return true;
  }
  if (machineType === 'HAAS' && machineConnectionType === 'ETHERNET') {
    return true;
  }
  return false;
};
